import { FC, useEffect, useState } from "react";
import { IScheduleDateOverride, scheduleService, ISlotPattern, ITutorAvailability } from "../../../../entities/schedule";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useMyActions } from "../../../../entities/my";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { ScheduleWeekGrid } from "../weekGrid/ScheduleWeekGrid";
import { ScheduleDateOverrides } from "../dateOverrides/ScheduleDateOverrides";
import classes from '../scheduleCalendar.module.scss'

interface IProps {
    userId: string;
    title?: string;
    subtitle?: string;
    hideTitle?: boolean;
}

export const TutorAvailabilityCalendar: FC<IProps> = ({
    userId,
    title = 'График доступности исполнителя',
    subtitle,
    hideTitle = false,
}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [availability, setAvailability] = useState<ITutorAvailability | null>(null)
    const [weeklySlots, setWeeklySlots] = useState<ISlotPattern[]>([])
    const [dateOverrides, setDateOverrides] = useState<IScheduleDateOverride[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState('')

    const canUpdate = availability?.availableActions.includes('SCHEDULE_ACTION_UPDATE') ?? false

    useEffect(() => {
        let isActual = true

        const getData = async() => {
            setIsLoading(true)
            setError('')

            try{
                const response = await scheduleService.getTutorAvailability({userId})

                if(!isActual) return

                setAvailability(response.availability)
                setWeeklySlots(response.availability.weeklySlots)
                setDateOverrides(response.availability.dateOverrides)
            }
            catch(error){
                console.log(error)
                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setError('Ошибка при получении графика исполнителя')
                    setGlobalMessage({message: 'Ошибка при получении графика исполнителя', type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [userId])

    const saveAvailability = async() => {
        if(!availability || !canUpdate) return

        if(!weeklySlots.length){
            setError('Выберите хотя бы один рабочий слот исполнителя')
            return
        }

        const overrideDateKeys = dateOverrides.map(item => (
            `${item.date.year}-${item.date.month}-${item.date.day}`
        ))

        if(dateOverrides.some(item => !item.date.year || !item.date.month || !item.date.day)){
            setError('Проверьте даты исключений')
            return
        }

        if(new Set(overrideDateKeys).size !== overrideDateKeys.length){
            setError('Одна дата не может быть добавлена в исключения несколько раз')
            return
        }

        setIsSaving(true)
        setError('')

        try{
            const response = await scheduleService.updateTutorAvailability({
                userId,
                weeklySlots,
                dateOverrides,
            })

            setAvailability(response.availability)
            setWeeklySlots(response.availability.weeklySlots)
            setDateOverrides(response.availability.dateOverrides)
            setGlobalMessage({message: 'График исполнителя сохранён', type: 'ok'})
        }
        catch(error){
            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError){
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setError('Ошибка при сохранении графика исполнителя')
                setGlobalMessage({message: 'Ошибка при сохранении графика исполнителя', type: 'error'})
            }
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <section>
                    {!hideTitle && <h2 className={classes.title}>{title}</h2>}
                    {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
                    <p className={classes.subtitle}>
                        Серые слоты не входят в рабочий график. Зелёные свободны для занятий,
                        чёрные заняты регулярным графиком ученика.
                    </p>
                    <p className={classes.subtitle}>Все слоты указываются по МСК.</p>
                </section>
                <ul className={classes.legend} aria-label="Обозначения графика">
                    <li className={`${classes.legendItem} ${classes.disabled}`}>Не в рабочем графике</li>
                    <li className={`${classes.legendItem} ${classes.selected}`}>Свободно для занятия</li>
                    <li className={`${classes.legendItem} ${classes.occupied}`}>Занято учеником</li>
                </ul>
            </section>

            {error && <p className={classes.error}>{error}</p>}

            {isLoading ?
                <section className={classes.message}><LoaderSpinner /></section>
                :
                <>
                    <ScheduleWeekGrid
                        value={weeklySlots}
                        occupiedSlots={availability?.occupiedSlots ?? []}
                        onChange={value => {
                            setWeeklySlots(value)
                            setError('')
                        }}
                        editable={canUpdate}
                    />

                    <ScheduleDateOverrides
                        value={dateOverrides}
                        onChange={value => {
                            setDateOverrides(value)
                            setError('')
                        }}
                        editable={canUpdate}
                    />

                    {!canUpdate &&
                        <p className={classes.message}>График доступен только для просмотра. Редактировать может куратор.</p>
                    }

                    {canUpdate &&
                        <section className={classes.actions}>
                            <button type="button" onClick={saveAvailability} disabled={isSaving}>
                                {isSaving ? 'Сохраняем...' : 'Сохранить график'}
                            </button>
                        </section>
                    }
                </>
            }
        </section>
    )
}
